// Parent view of a child's finances: account summary, fee breakdown,
// échéancier, submitted bank transfers, and virement upload on behalf of the
// child. Mirrors the student "Mon Compte Financier" page, child-scoped.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Wallet,
  CheckCircle,
  Clock,
  XCircle,
  Upload,
  Download,
  Calendar,
  Loader2,
  Receipt,
} from 'lucide-react';
import { ChildSelector } from '../components/ChildSelector';
import { fetchChildFinancialAccounts, fetchChildBankTransfers } from '../services/parentService';
import { formatCurrency, formatPaymentMethod, downloadMyReceipt, type StudentFinancialAccount } from '@/services/financialService';
import { BankTransferUploadModal } from '@/features/student/components/BankTransferUploadModal';

const formatShortDate = (date: string): string =>
  new Date(date).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', year: 'numeric' });

const transferStatusStyle: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-green-50 text-green-700',
  REJECTED: 'bg-red-50 text-red-700',
};

const transferStatusLabel: Record<string, string> = {
  PENDING: 'En attente',
  APPROVED: 'Validé',
  REJECTED: 'Rejeté',
};

const installmentStatusStyle: Record<string, string> = {
  PAID: 'bg-green-50 text-green-700',
  PARTIAL: 'bg-blue-50 text-blue-700',
  PENDING: 'bg-gray-100 text-gray-600',
  OVERDUE: 'bg-red-50 text-red-700',
};

const installmentStatusLabel: Record<string, string> = {
  PAID: 'Payée',
  PARTIAL: 'Partielle',
  PENDING: 'À venir',
  OVERDUE: 'En retard',
};

const ParentPaymentsPage: React.FC = () => {
  const [childId, setChildId] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<string | null>(null);

  // Receipt PDF is generated on demand; show progress, save straight to Downloads.
  const handleDownloadReceipt = async (paymentId: string, receiptNumber?: string): Promise<void> => {
    setDownloadingReceiptId(paymentId);
    try {
      await downloadMyReceipt(paymentId, `recu-${receiptNumber || paymentId}.pdf`);
    } catch {
      window.alert('Reçu indisponible pour ce paiement');
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const {
    data: accounts = [],
    isLoading: isLoadingAccounts,
    error: accountsError,
    refetch: refetchAccounts,
  } = useQuery({
    queryKey: ['parent-child-financial-accounts', childId],
    queryFn: () => fetchChildFinancialAccounts(childId as string),
    enabled: !!childId,
  });

  const {
    data: transfers = [],
    refetch: refetchTransfers,
  } = useQuery({
    queryKey: ['parent-child-bank-transfers', childId],
    queryFn: () => fetchChildBankTransfers(childId as string),
    enabled: !!childId,
  });

  // Prefer the current academic year's account, fall back to the most recent
  const account: StudentFinancialAccount | undefined =
    accounts.find((a) => a.academicYear?.isCurrent) ?? accounts[0];

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    refetchAccounts();
    refetchTransfers();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paiements</h1>
          <p className="text-gray-600 mt-1">
            Suivez les frais de scolarité de votre enfant et soumettez vos virements bancaires
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ChildSelector onChildChange={setChildId} />
          {account && account.balance > 0 && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all"
            >
              <Upload className="w-4 h-4" />
              Soumettre un virement
            </button>
          )}
        </div>
      </div>

      {!childId ? null : isLoadingAccounts ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : accountsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">
            {accountsError instanceof Error ? accountsError.message : 'Erreur lors du chargement du compte financier'}
          </p>
        </div>
      ) : !account ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wallet className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Aucun compte financier</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Le compte financier de votre enfant sera créé automatiquement dès que les frais de
            scolarité de sa filière seront configurés par l'établissement.
          </p>
        </div>
      ) : (
        <>
          {/* Academic year */}
          {account.academicYear?.name && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Année académique {account.academicYear.name}
            </p>
          )}

          {/* Summary cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-500">Net à payer</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(account.totalDue - account.totalDiscount)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-500">Déjà payé</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(account.totalPaid)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-500">Remises</p>
              <p className="text-2xl font-bold text-indigo-600 mt-1">{formatCurrency(account.totalDiscount)}</p>
            </div>
            <div className={`rounded-xl border p-5 ${account.balance > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <p className="text-sm font-medium text-gray-600">Reste à payer</p>
              <p className={`text-2xl font-bold mt-1 ${account.balance > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                {formatCurrency(Math.max(0, account.balance))}
              </p>
            </div>
          </div>

          {/* Fee breakdown (fees + discounts) */}
          {((account.feeItems?.length ?? 0) > 0 || (account.discounts?.length ?? 0) > 0) && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-gray-500" />
                <h3 className="font-semibold text-gray-900">Détail des frais</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {(account.feeItems ?? []).map((item) => (
                  <div key={item.id} className="px-5 py-3 flex items-center justify-between">
                    <p className="text-sm text-gray-700">{item.label}</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(item.amount)}</p>
                  </div>
                ))}
                {/* Remises / bourses shown as deductions */}
                {(account.discounts ?? []).map((discount) => (
                  <div key={discount.id} className="px-5 py-3 flex items-center justify-between bg-purple-50/60">
                    <p className="text-sm text-purple-700">
                      Remise — {discount.discountConfig?.name || 'Réduction'}
                    </p>
                    <p className="text-sm font-semibold text-purple-600">
                      -{formatCurrency(discount.discountAmount)}
                    </p>
                  </div>
                ))}
                {(account.discounts?.length ?? 0) > 0 && (
                  <div className="px-5 py-3 flex items-center justify-between bg-gray-50">
                    <p className="text-sm font-semibold text-gray-700">Net à payer</p>
                    <p className="text-sm font-bold text-gray-900">
                      {formatCurrency(account.totalDue - account.totalDiscount)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Échéancier */}
          {account.schedule && (account.schedule.installments?.length ?? 0) > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Échéancier de paiement</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Échéance</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date limite</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Montant</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Payé</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {account.schedule.installments!.map((inst) => (
                      <tr key={inst.id}>
                        <td className="px-5 py-3 text-sm text-gray-900">{inst.label}</td>
                        <td className="px-5 py-3 text-sm text-gray-600">{formatShortDate(inst.dueDate)}</td>
                        <td className="px-5 py-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(inst.amountDue)}</td>
                        <td className="px-5 py-3 text-sm text-right text-gray-600">{formatCurrency(inst.amountPaid)}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold ${installmentStatusStyle[inst.status] ?? 'bg-gray-100 text-gray-600'}`}>
                            {installmentStatusLabel[inst.status] ?? inst.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
              <p className="text-sm text-gray-500">
                Aucun échéancier n'a encore été assigné au compte de votre enfant.
              </p>
            </div>
          )}

          {/* Recorded payments with official receipt download */}
          {(account.payments?.length ?? 0) > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Paiements réglés</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Montant</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Méthode</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Reçu</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(account.payments ?? []).map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm text-gray-900">{formatShortDate(payment.paymentDate)}</td>
                        <td className="px-5 py-3 text-sm font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                        <td className="px-5 py-3 text-sm text-gray-600">{formatPaymentMethod(payment.method)}</td>
                        <td className="px-5 py-3 text-sm">
                          {payment.status !== 'CANCELLED' ? (
                            <button
                              onClick={() => handleDownloadReceipt(payment.id, payment.receiptNumber)}
                              disabled={downloadingReceiptId === payment.id}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60 disabled:cursor-wait"
                              title="Télécharger le reçu"
                            >
                              {downloadingReceiptId === payment.id ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</>
                              ) : (
                                <><Download className="w-4 h-4" /> Télécharger</>
                              )}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Transfers history */}
          {transfers.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Virements soumis</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Montant</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Banque</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Soumis par</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Justificatif</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {transfers.map((transfer) => (
                      <tr key={transfer.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm text-gray-900">{formatShortDate(transfer.transferDate)}</td>
                        <td className="px-5 py-3 text-sm font-semibold text-gray-900">{formatCurrency(transfer.amount)}</td>
                        <td className="px-5 py-3 text-sm text-gray-600">{transfer.bankName || '-'}</td>
                        <td className="px-5 py-3 text-sm text-gray-600">
                          {transfer.submittedByRole === 'PARENT' ? 'Vous' : transfer.submittedByRole === 'STUDENT' ? 'Votre enfant' : 'Administration'}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${transferStatusStyle[transfer.status] ?? 'bg-gray-100 text-gray-600'}`}>
                            {transfer.status === 'PENDING' && <Clock className="w-3 h-3" />}
                            {transfer.status === 'APPROVED' && <CheckCircle className="w-3 h-3" />}
                            {transfer.status === 'REJECTED' && <XCircle className="w-3 h-3" />}
                            {transferStatusLabel[transfer.status] ?? transfer.status}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {transfer.receiptUrl && (
                            <a
                              href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${transfer.receiptUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              <Download className="w-4 h-4" />
                              Voir
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Rejected transfers hint */}
          {transfers.some((t) => t.status === 'REJECTED') && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Certains virements ont été rejetés</p>
              <p className="text-sm text-red-700 mt-1">
                Vérifiez le motif auprès de l'administration financière puis soumettez un nouveau virement si nécessaire.
              </p>
            </div>
          )}

          {showUploadModal && childId && (
            <BankTransferUploadModal
              accountId={account.id}
              studentId={childId}
              balance={account.balance}
              onClose={() => setShowUploadModal(false)}
              onSuccess={handleUploadSuccess}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ParentPaymentsPage;
